import { useState } from "react";
import mixpanel from "mixpanel-browser";
import { useTimeoutFn } from "react-use";

import cn from "app/util/classnames";
import createFeedback from "app/feedback/createFeedback";
import useSailthru, { getCurrentDate } from "app/game-over/useSailThru";
import useResetableStorage from "app/util/useResetableStorage";

import DialogModal from "app/game-over/DialogModal";
import PrivacyPolicy from "app/game-over/PrivacyPolicy";
import MenuButton from "app/ui/MenuButton";

interface UseRateLimitedTimeoutFnParams {
  storeKey: string;
  id?: string;
  fn: () => void;
  maxTries?: number;
  timeout?: number;
}

function useRateLimitedTimeoutFn({
  storeKey,
  id,
  maxTries = 3,
  fn,
  timeout = 3000,
}: UseRateLimitedTimeoutFnParams) {
  const [disabled, setDisabled] = useResetableStorage<boolean>(
    `${storeKey}_DISABLED`,
    false,
  );

  const [ids = [], setIds] = useResetableStorage<string[]>(
    `${storeKey}_IDS`,
    [],
  );

  const [ready, cancel, reset] = useTimeoutFn(
    () => {
      if (
        !id ||
        ids.includes(id) ||
        ids.length >= maxTries ||
        disabled === true
      ) {
        return;
      }

      fn();
      setIds((gameIds = []) => [...new Set([...gameIds, id])]);
    },
    !disabled ? timeout : undefined,
  );

  return {
    disable: () => {
      setDisabled(true);
      cancel();
    },
    ready,
    cancel,
    reset,
  };
}

interface NPSFeedbackBannerProps {
  timeout?: number;
  id?: string;
}

export default function NPSFeedbackDialog({
  timeout = 3000,
  id,
}: NPSFeedbackBannerProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const { signUp } = useSailthru();

  const { disable } = useRateLimitedTimeoutFn({
    storeKey: "PIXELLENCE_NPS",
    fn: () => setIsOpen(true),
    id,
    timeout,
  });

  function closeDialog() {
    setIsOpen(false);
    setError("");
  }

  return (
    <DialogModal
      className="mb-0 mt-auto sm:my-auto"
      isOpen={isOpen}
      onClose={() => {
        setError("");
      }}
    >
      <div className="mx-auto grid w-full max-w-131 bg-green-mid px-3 py-8">
        <div
          className={cn(
            "col-start-1 row-start-1 flex w-full flex-col items-center justify-center gap-8 transition-opacity",
            !success ? "invisible opacity-0" : "opacity-100",
          )}
        >
          <h2 className="font-condensed text-3.5xl max-w-64 text-center font-bold uppercase leading-none -tracking-[0.015em]">
            Thank you for your feedback!
          </h2>
          <MenuButton
            type="button"
            onClick={closeDialog}
            className="h-10 w-49.5 text-sm uppercase"
          >
            Continue
          </MenuButton>
        </div>
        <form
          className={cn(
            "col-start-1 row-start-1 flex w-full flex-col gap-4 border-0",
            success && "invisible",
          )}
          onSubmit={(e) => {
            e.preventDefault();
            setError("");

            const formData = new FormData(e.target as HTMLFormElement);
            const email = formData.get("email") as string;
            const nps = formData.get("nps") as string;
            const feedback = formData.get("feedback") as string;

            if (!email && !nps && !feedback) {
              closeDialog();
              return;
            }

            try {
              if (email !== "") {
                signUp({
                  email,
                  lists: { Pixellence_Feedback: 1 },
                  vars: !nps
                    ? undefined
                    : {
                        pixellence_nps: nps,
                        pixellence_feedback_optin_date: getCurrentDate(),
                        pixellence_feedback_source: "nps",
                      },
                });
              }

              if (feedback !== "") {
                createFeedback({
                  feedback,
                  email: !email ? undefined : email,
                  nps: !nps ? undefined : Number(nps),
                });
              }

              mixpanel.track("nps_click", {
                value: !nps ? undefined : nps,
                email: !email ? undefined : email,
              });

              disable();
              setError("");
              setSuccess(true);
            } catch (error) {
              setError(`${error}`);
            }
          }}
        >
          <h2 className="font-condensed -my-[0.15em] text-center text-2xl font-bold uppercase leading-none -tracking-[0.36px] mb-2">
            Tell us about <span className="inline-block h-px w-px" /> your
            play&nbsp;experience!
          </h2>

          <div className="flex flex-col p-3">
            <fieldset className="contents">
              <legend className="float-start -my-[0.15em] block p-0 text-base leading-none -tracking-[0.015em]">
                How likely are you to recommend this game to a friend or
                colleague?
              </legend>
              <div className="mt-5 flex justify-between rounded-3xl border border-green-darker bg-white/10">
                {Array.from({ length: 11 }, (_, index) => index).map(
                  (value) => (
                    <label
                      key={value}
                      htmlFor={`nps-${value}`}
                      className="cursor-pointer"
                    >
                      <input
                        type="radio"
                        id={`nps-${value}`}
                        name="nps"
                        value={value}
                        className="peer absolute opacity-0"
                        aria-labelledby={`label-nps-${value} ${
                          value === 1 ? "low" : value === 10 ? "high" : ""
                        }`}
                      />
                      <span className="flex h-10 w-6 items-center justify-center rounded-full border border-transparent text-center peer-checked:border-green-darker peer-checked:bg-green-mid sm:w-10">
                        {value}
                      </span>
                    </label>
                  ),
                )}
              </div>
              <div className="mt-3 flex justify-between text-sm leading-[0.7rem]">
                <span id="low">Not at all likely</span>
                <span id="high">Extremely likely</span>
              </div>
            </fieldset>
          </div>

          <div className="flex w-full flex-col p-3">
            <label
              htmlFor="feedback"
              className="-my-[0.15em] text-base leading-none -tracking-[0.015em]"
            >
              What would improve Pixellence?
            </label>

            <textarea
              name="feedback"
              placeholder="Let us know..."
              className={cn(
                "relative mt-5 h-20 p-2 w-full resize-none",
                "appearance-none rounded-none border border-green-dark shadow bg-white/10",
                "placeholder:text-green-dark inset-shadow-2xs",
              )}
            />
          </div>

          <div className="flex w-full flex-col p-3">
            <label
              htmlFor="feedback"
              className="-my-[0.15em] text-base leading-none -tracking-[0.015em]"
            >
              Interested in helping improve the game?
            </label>

            <input
              name="email"
              type="email"
              placeholder="Enter your email"
              onChange={() => setError("")}
              className="rounded-0 mt-3 w-full appearance-none border border-green-dark bg-white/10 p-1.5 text-center placeholder:text-green-dar inset-shadow-2xs"
              aria-labelledby="email-error"
            />
            <label
              id="email-error"
              className={cn(
                "py-1 text-center text-xs font-medium -tracking-[0.01em]",
                !error && "absolute opacity-0",
              )}
            >
              {error}
            </label>
            <div className="h-2" role="presentation" />
            <PrivacyPolicy />
          </div>

          <div className="flex justify-center gap-5">
            <MenuButton
              className="h-10 rounded bg-green-darker text-sm"
              type="button"
              onClick={closeDialog}
            >
              CANCEL
            </MenuButton>
            <MenuButton className="h-10 text-sm">
              Submit
            </MenuButton>
          </div>
        </form>
      </div>
    </DialogModal>
  );
}
